import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { defaultIfEmpty, firstValueFrom, Observable, timeout } from 'rxjs';
import { RiderEventsService } from '@app/rest-client/faxe/services/rider-events.service';
import { FenceResultsService } from '@app/rest-client/faxe/services/fence-results.service';
import { RollingHistListService } from '@app/components/shared/rolling-hist-list/service/rolling-hist-list.service';
import { PostStatus } from '@app/components/shared/rolling-hist-list/model/list-item';
import { PasswordService } from '@app/services/password/password.service';

export type PostJobType = 'riderEvent' | 'fenceResult';

export interface PostJob {
  id: string;
  type: PostJobType;
  params: any;
  raceGuid: string;
  bucket: string;
  itemId: string;
  attempts: number;
  nextAttemptAt: number;
  createdAt: number;
}

export const POST_RETRY_STORAGE_KEY = 'app_postRetryQueue';
/** Delay before retry n (1-based) after the previous attempt failed (first retry is only held back by POST_MIN_GAP_MS). */
export const POST_RETRY_DELAYS_MS = [0, 5000, 10000, 30000];
export const POST_MAX_RETRIES = POST_RETRY_DELAYS_MS.length;
export const POST_TIMEOUT_MS = 15000;
export const POST_MIN_GAP_MS = 300;
export const POST_MAX_RETRY_AFTER_MS = 5 * 60 * 1000;
export const POST_JOB_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const POST_RETRY_TOMBSTONE_MAX_AGE_MS = 2 * POST_JOB_MAX_AGE_MS;
export const POST_QUEUE_MAX_SIZE = 200;

const RETRYABLE_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504]);

/**
 * Sends POSTs one at a time, keeps failed ones in client storage and retries
 * them with a fixed back-off (max {@link POST_MAX_RETRIES} retries).
 */
@Injectable({ providedIn: 'root' })
export class PostRetryService {
  private jobs: PostJob[] = [];
  private inFlight = false;
  private blockedUntil = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private initialized = false;
  private lockHeld = false;
  private pendingUpserts = new Map<string, PostJob>();
  private pendingRemovals = new Set<string>();
  private removedJobs = new Map<string, number>();

  constructor(
    private readonly riderEventsService: RiderEventsService,
    private readonly fenceResultsService: FenceResultsService,
    private readonly rollingHistListService: RollingHistListService,
    private readonly passwordService: PasswordService
  ) {}

  /** Restores persisted jobs and resumes retries when the page is visible again. */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.jobs = this.load();
    this.passwordService.tokens$.subscribe(() => this.resume());
    window.addEventListener('storage', (event) => {
      if (event.key === POST_RETRY_STORAGE_KEY) {
        this.resume();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.resume();
      }
    });
    window.addEventListener('online', () => this.resume());
    window.addEventListener('focus', () => this.resume());
    window.addEventListener('pageshow', () => this.resume());
    this.pump();
  }

  /** Checks if any retry is due and sends it. */
  resume(): void {
    this.pump();
  }

  enqueue(
    type: PostJobType,
    params: any,
    bucket: string,
    itemId: string
  ): void {
    if (this.jobs.length >= POST_QUEUE_MAX_SIZE) {
      console.error('[PostRetry] queue full, post rejected', type);
      this.setStatus({ bucket, itemId } as PostJob, 'failed');
      return;
    }
    const raceGuid = params?.raceGuid ?? params?.body?.raceGuid;
    if (typeof raceGuid !== 'string' || !raceGuid) {
      console.error('[PostRetry] race missing, post rejected', type);
      this.setStatus({ bucket, itemId } as PostJob, 'failed');
      return;
    }
    const now = Date.now();
    this.jobs.push({
      id: `${now}-${Math.random().toString(36).slice(2, 10)}`,
      type,
      params,
      raceGuid,
      bucket,
      itemId,
      attempts: 0,
      nextAttemptAt: now,
      createdAt: now,
    });
    const job = this.jobs[this.jobs.length - 1];
    this.pendingUpserts.set(job.id, job);
    this.save();
    this.pump();
  }

  pendingCount(): number {
    return this.jobs.length;
  }

  static isRetryable(status: number): boolean {
    return RETRYABLE_STATUS.has(status);
  }

  /** Parses Retry-After (seconds or HTTP date) to ms; undefined if absent/invalid. */
  static parseRetryAfter(value: string | null | undefined, now = Date.now()): number | undefined {
    if (value === null || value === undefined || value.trim() === '') {
      return undefined;
    }
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      return Math.min(+trimmed * 1000, POST_MAX_RETRY_AFTER_MS);
    }
    const date = Date.parse(trimmed);
    if (isNaN(date)) {
      return undefined;
    }
    return Math.min(Math.max(date - now, 0), POST_MAX_RETRY_AFTER_MS);
  }

  private pump(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    if (this.inFlight) {
      return;
    }
    if (this.lockHeld) {
      void this.pumpLocked();
      return;
    }
    void this.withQueueLock(async () => {
      this.lockHeld = true;
      try {
        this.persistLocked();
        await this.pumpLocked();
      } finally {
        this.lockHeld = false;
      }
    });
  }

  private async pumpLocked(): Promise<void> {
    if (this.inFlight) {
      return;
    }
    const now = Date.now();
    this.expireOldJobs(now);
    if (this.jobs.length === 0) {
      return;
    }
    const authorizedJobs = this.jobs.filter(
      (job) => job.raceGuid && this.passwordService.getToken(job.raceGuid)
    );
    if (authorizedJobs.length === 0) {
      return;
    }
    const nextDueAt = Math.max(
      Math.min(...authorizedJobs.map((j) => j.nextAttemptAt)),
      this.blockedUntil
    );
    if (nextDueAt > now) {
      this.timer = setTimeout(() => this.pump(), nextDueAt - now);
      return;
    }
    const job = authorizedJobs
      .filter((j) => j.nextAttemptAt <= now)
      .sort((a, b) => a.nextAttemptAt - b.nextAttemptAt)[0];
    if (job) {
      await this.execute(job);
    }
  }

  private async execute(job: PostJob): Promise<void> {
    this.inFlight = true;
    job.attempts++;
    this.pendingUpserts.set(job.id, job);
    this.save();
    try {
      await firstValueFrom(
        this.call(job).pipe(timeout(POST_TIMEOUT_MS), defaultIfEmpty(null))
      );
      this.remove(job);
      this.setStatus(job, 'verified');
    } catch (err) {
      this.onFailure(job, err);
    } finally {
      this.inFlight = false;
      this.blockedUntil = Math.max(this.blockedUntil, Date.now() + POST_MIN_GAP_MS);
      this.save();
      await this.pumpLocked();
    }
  }

  private onFailure(job: PostJob, err: unknown): void {
    const httpError = err instanceof HttpErrorResponse ? err : undefined;
    const status = httpError ? httpError.status : 0;
    const retriesMade = job.attempts - 1;
    const serverDelay =
      status === 429 || status === 503
        ? PostRetryService.parseRetryAfter(
            httpError?.headers?.get('Retry-After')
          )
        : undefined;
    if (status === 429 && serverDelay !== undefined) {
      this.blockedUntil = Math.max(this.blockedUntil, Date.now() + serverDelay);
    }

    if (!PostRetryService.isRetryable(status)) {
      console.error(
        `[PostRetry] ${job.type} failed with HTTP ${status}, not retryable. Retries made: ${retriesMade}/${POST_MAX_RETRIES}`,
        err
      );
      this.remove(job);
      this.setStatus(job, 'failed');
      return;
    }
    if (retriesMade >= POST_MAX_RETRIES) {
      console.error(
        `[PostRetry] ${job.type} failed with HTTP ${status}, giving up. Retries made: ${retriesMade}/${POST_MAX_RETRIES}`,
        err
      );
      this.remove(job);
      this.setStatus(job, 'failed');
      return;
    }

    let delay = POST_RETRY_DELAYS_MS[retriesMade];
    let source = 'schedule';
    if (serverDelay !== undefined) {
      delay = serverDelay;
      source = 'server Retry-After';
    }
    job.nextAttemptAt = Date.now() + delay;
    this.pendingUpserts.set(job.id, job);
    this.save();
    this.setStatus(job, 'retrying');
    console.log(
      `[PostRetry] ${job.type} failed with HTTP ${status}. Retries made: ${retriesMade}/${POST_MAX_RETRIES}. Next retry (${retriesMade + 1}/${POST_MAX_RETRIES}) in ${delay / 1000}s (${source})`
    );
  }

  private call(job: PostJob): Observable<any> {
    switch (job.type) {
      case 'riderEvent':
        return this.riderEventsService.racesRaceGuidHorsecombinationsHorseCombinationNumberRidersRidereventsPost(
          job.params
        );
      case 'fenceResult':
        return this.fenceResultsService.fenceResultPost(job.params);
    }
  }

  private expireOldJobs(now: number): void {
    const expired = this.jobs.filter((j) => now - j.createdAt > POST_JOB_MAX_AGE_MS);
    if (expired.length === 0) {
      return;
    }
    expired.forEach((j) => {
      console.error(`[PostRetry] ${j.type} expired after retries, dropped`);
      this.remove(j);
      this.setStatus(j, 'failed');
    });
  }

  private remove(job: PostJob): void {
    this.jobs = this.jobs.filter((j) => j.id !== job.id);
    this.pendingUpserts.delete(job.id);
    this.pendingRemovals.add(job.id);
    this.removedJobs.set(job.id, Date.now());
    this.save();
  }

  private setStatus(job: PostJob, status: PostStatus): void {
    this.rollingHistListService.setStatus(job.bucket, job.itemId, status);
  }

  private load(): PostJob[] {
    const stored = this.readQueue();
    this.blockedUntil = stored.blockedUntil;
    this.removedJobs = new Map(
      stored.removedJobs.map(({ id, removedAt }) => [id, removedAt])
    );
    return stored.jobs.filter((job) => !this.removedJobs.has(job.id));
  }

  private readQueue(): {
    jobs: PostJob[];
    blockedUntil: number;
    removedJobs: { id: string; removedAt: number }[];
  } {
    try {
      const json = window.localStorage.getItem(POST_RETRY_STORAGE_KEY);
      const parsed = json ? JSON.parse(json) : [];
      const queue = Array.isArray(parsed)
        ? { jobs: parsed, blockedUntil: 0, removedJobs: [] }
        : parsed;
      return {
        jobs: Array.isArray(queue?.jobs)
          ? queue.jobs
              .filter(
                (j) =>
                  j &&
                  typeof j.id === 'string' &&
                  (j.type === 'riderEvent' || j.type === 'fenceResult') &&
                  typeof (
                    j.raceGuid ??
                    j.params?.raceGuid ??
                    j.params?.body?.raceGuid
                  ) === 'string' &&
                  typeof j.bucket === 'string' &&
                  typeof j.itemId === 'string' &&
                  Number.isFinite(j.attempts) &&
                  Number.isFinite(j.nextAttemptAt) &&
                  Number.isFinite(j.createdAt)
              )
              .map((job) => ({
                ...job,
                raceGuid:
                  job.raceGuid ??
                  job.params?.raceGuid ??
                  job.params?.body?.raceGuid,
              }))
          : [],
        blockedUntil: Number.isFinite(queue?.blockedUntil)
          ? queue.blockedUntil
          : 0,
        removedJobs: Array.isArray(queue?.removedJobs)
          ? queue.removedJobs.filter(
              (removed) =>
                typeof removed?.id === 'string' &&
                Number.isFinite(removed.removedAt)
            )
          : [],
      };
    } catch {
      return { jobs: [], blockedUntil: 0, removedJobs: [] };
    }
  }

  private save(): void {
    if (this.lockHeld) {
      this.persistLocked();
      return;
    }
    void this.withQueueLock(async () => {
      this.lockHeld = true;
      try {
        this.persistLocked();
      } finally {
        this.lockHeld = false;
      }
    });
  }

  private persistLocked(): void {
    try {
      const stored = this.readQueue();
      const removedAt = new Map<string, number>();
      for (const removed of stored.removedJobs) {
        if (Date.now() - removed.removedAt <= POST_RETRY_TOMBSTONE_MAX_AGE_MS) {
          removedAt.set(removed.id, removed.removedAt);
        }
      }
      for (const [id, timestamp] of this.removedJobs) {
        if (Date.now() - timestamp <= POST_RETRY_TOMBSTONE_MAX_AGE_MS) {
          removedAt.set(id, Math.max(removedAt.get(id) ?? 0, timestamp));
        }
      }
      for (const id of this.pendingRemovals) {
        removedAt.set(id, Date.now());
      }

      const jobs = new Map(
        stored.jobs
          .filter((job) => !removedAt.has(job.id))
          .map((job) => [job.id, job])
      );
      for (const [id, job] of this.pendingUpserts) {
        if (!removedAt.has(id)) {
          jobs.set(id, job);
        }
      }
      this.jobs = [...jobs.values()];
      this.removedJobs = removedAt;
      this.blockedUntil = Math.max(this.blockedUntil, stored.blockedUntil);
      window.localStorage.setItem(
        POST_RETRY_STORAGE_KEY,
        JSON.stringify({
          jobs: this.jobs,
          blockedUntil: this.blockedUntil,
          removedJobs: [...this.removedJobs].map(([id, removedAt]) => ({
            id,
            removedAt,
          })),
        })
      );
      this.pendingUpserts.clear();
      this.pendingRemovals.clear();
    } catch (e) {
      console.warn('[PostRetry] could not persist queue', e);
    }
  }

  private withQueueLock(callback: () => Promise<void>): Promise<void> {
    const locks = (window.navigator as Navigator & { locks?: LockManager })
      .locks;
    return locks
      ? locks.request<void>(
          POST_RETRY_STORAGE_KEY,
          { mode: 'exclusive' },
          callback
        )
      : callback();
  }
}
